import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Barcode, CheckCircle2, TriangleAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  EmptyState,
  Section,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { applyColourCodes, fetchColourCodePlan } from '../lib/api/catalog.ts'
import { ApiError } from '../lib/api.ts'

/**
 * Acertar os códigos das cores que já estão cadastradas — F181.
 *
 * ## O pedido
 *
 * O dono, depois de criar seis cores do CHASSI G05: *"a cor aqui vai ter que
 * gerar o código automático e o código de barra. Exemplo: o código do produto
 * é 6455; se for a primeira cor do produto vai criar assim 6455-1"*.
 *
 * De agora em diante nasce assim sozinho. O que já está cadastrado é o que
 * esta tela resolve: a cor PADRÃO — a que ficou com o código puro do produto —
 * ganha o `-1`, e toda cor sem código de barras ganha o interno da loja.
 *
 * ## Por que a prévia não é opcional
 *
 * Renomear o código de uma peça que está na prateleira é diferente de criar
 * uma: existe etiqueta impressa, planilha do contador e gente que decorou o
 * número. O "de → para" aparece inteiro antes, e só então o botão grava. É o
 * mesmo contrato da recodificação por planilha (F165), pela mesma razão.
 *
 * ## O que ela NÃO toca
 *
 * Produto de uma cor só — que é produto sem cor nenhuma, e cujo código não é
 * assunto daqui. O código de barras que já existe também fica: barra impressa
 * está em etiqueta na gaveta, e o que a leitora lê tem de continuar valendo.
 */
export const ColourCodesPanel = ({
  canWrite,
}: {
  canWrite: boolean
}): ReactNode => {
  const client = useQueryClient()
  const [applied, setApplied] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const plan = useQuery({
    queryKey: ['colour-code-plan'],
    queryFn: async ({ signal }) => await fetchColourCodePlan(signal),
    enabled: canWrite,
  })

  const apply = useMutation({
    mutationFn: applyColourCodes,
    onSuccess: async (result) => {
      setApplied(result.total)
      setError(null)
      // O catálogo inteiro mudou de código: a lista de produtos, a de estoque
      // e a ficha aberta ao lado estão todas com o número velho.
      await client.invalidateQueries()
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível acertar os códigos.'
      )
    },
  })

  if (!canWrite) return null

  if (plan.isPending) {
    return (
      <div className="flex items-center gap-2 text-(--text-secondary)">
        <Spinner className="h-4 w-4" />
        <span className="text-(length:--text-sm)">Conferindo as cores…</span>
      </div>
    )
  }

  if (plan.isError) {
    return (
      <p role="alert" className="text-(length:--text-sm) text-(--danger)">
        Não foi possível conferir as cores.
      </p>
    )
  }

  const data = plan.data

  if (applied !== null) {
    return (
      <Section
        title="Códigos acertados"
        description={
          applied === 0
            ? 'Nada precisava mudar.'
            : `${applied.toLocaleString('pt-BR')} ${applied === 1 ? 'cor teve' : 'cores tiveram'} o código ou o código de barras acertado.`
        }
      >
        <div className="flex items-center gap-2">
          <CheckCircle2
            aria-hidden="true"
            className="h-5 w-5 text-(--success)"
          />
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            As etiquetas impressas continuam valendo: nenhum código de barras
            que já existia foi trocado.
          </p>
        </div>
      </Section>
    )
  }

  if (data.total === 0) {
    return (
      <EmptyState
        icon={<Barcode aria-hidden="true" className="h-6 w-6" />}
        title="Os códigos já estão certos"
        description="Toda cor tem o número dela no código e um código de barras. Nada a acertar."
      />
    )
  }

  return (
    <Section
      title="Acertar os códigos das cores"
      description="A primeira cor passa a ter o -1 no código, e quem está sem código de barras ganha o interno da loja."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile
          label="Produtos"
          value={data.products.toLocaleString('pt-BR')}
        />
        <StatTile
          label="Códigos"
          value={data.renames.toLocaleString('pt-BR')}
        />
        <StatTile
          label="Códigos de barras"
          value={data.barcodes.toLocaleString('pt-BR')}
        />
      </div>

      {data.blocked.length > 0 && (
        <div
          role="status"
          className="flex gap-2 rounded-(--radius-md) border border-(--warning) bg-(--surface-sunken) p-3 text-(length:--text-sm)"
        >
          <TriangleAlert
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-(--warning)"
          />
          <p className="text-(--text-secondary)">
            {data.blocked.length === 1
              ? 'Um produto fica como está: '
              : `${String(data.blocked.length)} produtos ficam como estão: `}
            {data.blocked
              .slice(0, 5)
              .map(
                (entry) =>
                  `${entry.productName} (${entry.wanted} já é de outra peça)`
              )
              .join('; ')}
            . Nenhum código é tirado de quem já o tem.
          </p>
        </div>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Produto</TableHeaderCell>
              <TableHeaderCell>Cor</TableHeaderCell>
              <TableHeaderCell>Código</TableHeaderCell>
              <TableHeaderCell>Passa a ser</TableHeaderCell>
              <TableHeaderCell>Código de barras</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.changes.map((change) => (
              <TableRow key={`${change.sku}-${change.newSku}`}>
                <TableCell>{change.productName}</TableCell>
                <TableCell>{change.colourName ?? '—'}</TableCell>
                <TableCell className="font-(--font-mono)">
                  {change.sku}
                </TableCell>
                <TableCell className="font-(--font-mono)">
                  {change.newSku === change.sku ? (
                    <span className="text-(--text-muted)">fica</span>
                  ) : (
                    change.newSku
                  )}
                </TableCell>
                <TableCell>
                  {change.generatesGtin ? (
                    'gera'
                  ) : (
                    <span className="text-(--text-muted)">fica</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {data.total > data.changes.length && (
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          Mostrando as primeiras {data.changes.length.toLocaleString('pt-BR')}{' '}
          de {data.total.toLocaleString('pt-BR')}.
        </p>
      )}

      {error !== null && (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      <div>
        <Button
          type="button"
          variant="primary"
          loading={apply.isPending}
          leadingIcon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            apply.mutate()
          }}
        >
          {apply.isPending ? 'Acertando…' : 'Acertar os códigos'}
        </Button>
      </div>
    </Section>
  )
}
